import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/api/client'
import { SeaBackdrop } from '@/components/SeaBackdrop'
import { SeoHead } from '@/components/SeoHead'
import { SEASON_LENGTH_WEEKS } from '@/config'

interface LocationState {
  from?: { pathname: string }
}

/**
 * /login — maritime "ship's log" sign-in over the shared <SeaBackdrop/>.
 * Form logic is unchanged from the original; only the presentation is new.
 */
export function Login() {
  const { login, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as LocationState)?.from?.pathname ?? '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (isLoading) return null
  if (isAuthenticated) return <Navigate to={from} replace />

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login({ email, password })
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Connexion échouée')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="relative min-h-[calc(100vh-8rem)] overflow-hidden flex items-start lg:items-center justify-center px-6 pt-10 sm:pt-16 lg:pt-0">
      <SeoHead title="Se connecter · Corsalia" noindex />
      <SeaBackdrop shipAlign="side" />

      <div className="relative z-10 w-full max-w-md">
        <div className="rounded-2xl border border-sand-500/25 bg-ocean-900/70 backdrop-blur-md shadow-[0_30px_70px_-30px_rgba(0,0,0,0.8)] p-7 sm:p-8">
          <div className="text-center mb-6">
            <h1 className="font-display text-3xl text-sand-300">Bon retour à bord</h1>
            <p className="text-sm text-ocean-200 mt-1">Reprends le cap, capitaine.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <Field
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              autoFocus
              required
            />
            <Field
              id="password"
              label="Mot de passe"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
            />

            {error && (
              <div className="text-rum-500 text-sm bg-rum-700/20 border border-rum-500/30 rounded-lg p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-2 px-4 py-3 rounded-lg bg-gradient-to-b from-sand-300 to-sand-500 text-ocean-900 font-display font-bold text-lg ring-1 ring-sand-100/50
                         hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
            >
              {submitting ? 'Connexion…' : 'Embarquer'}
            </button>
          </form>

          <p className="text-center text-sm text-ocean-200 mt-6">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-sand-300 hover:text-sand-100 underline underline-offset-4">
              Créer un capitaine
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-ocean-400 mt-6">
          Pas de pay-to-win · Saison de {SEASON_LENGTH_WEEKS} semaines, puis tout repart à zéro.
        </p>
      </div>
    </section>
  )
}

/** Maritime-styled labelled input used across the auth screens. */
export function Field({
  id,
  label,
  type = 'text',
  value,
  onChange,
  hint,
  error,
  ...rest
}: {
  id: string
  label: string
  type?: string
  value: string
  onChange: (v: string) => void
  hint?: string
  error?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id' | 'type' | 'value' | 'onChange'>) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-ocean-100 mb-1">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-ocean-900/80 border border-ocean-500/40 text-ocean-50 placeholder-ocean-400/70 focus:border-sand-300 focus:outline-none transition-colors"
        {...rest}
      />
      {hint && <p className="text-xs text-ocean-300 mt-1">{hint}</p>}
      {error && <p className="text-rum-500 text-xs mt-1">{error}</p>}
    </div>
  )
}
